import React, { useState, useEffect, useRef, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import axios from 'utils/api/axios'
import AxiosUtils from 'utils/api/axiosUtils'
import { Message, MessageSeverity } from 'constants/message'
import { HTTP_CODES } from 'constants/constants'
import DonutWorkload from './components/donutWorkload'
import './widgetWorkload.css'
import LangContext from 'context/LangContext'
import MessageContext from 'context/MessageContext'
import LoadingContext from 'context/LoadingContext'
import UrlApis from 'utils/api/urlApis'
import AuthContext from 'context/AuthContext'
import { Typography } from '@airbus/components-react/components'

// Colours attached to each labelled section of the donut
const coloursForLabels = [
  'rgb(84,209,81)',
  'rgb(186,230,70)',
  'rgb(143,230,147)',
  'rgb(99,191,255)',
  'rgb(64,171,199)',
  'rgb(65,216,238)',
  'rgb(156,243,250)',
  'rgb(189,247,251)',
  'rgb(247,225,178)',
  'rgb(255, 0, 0)',
]

const WidgetWorkload = ({ bdvnumdoc }) => {
  const { t } = useTranslation()

  const [data, setData] = useState()
  const [isLoadInProgressActive, setIsLoadInProgressActive] = useState(false)
  const [showLegend, setShowLegend] = useState(false)
  // Key counter for forcing doughnut redraw on widget resizing
  const { lang } = useContext(LangContext)
  const { handleDisplayMessage } = useContext(MessageContext)
  const { handleLoadInProgress } = useContext(LoadingContext)
  const { handleRequestLogin } = useContext(AuthContext)

  // Labels for donut's sections
  const labels = [
    t('9500110'),
    t('9500119'),
    t('9500098'),
    t('9500099'),
    t('9500100'),
    t('9500101'),
    t('9500102'),
    t('9500103'),
    t('9500104'),
    t('9500105'),
  ]

  /********
   * Synchronization of widget's data to keep track of them
   ********/

  const loadData = async () => {
    try {
      console.debug(
        'Widget <' +
          t('9500017') +
          '> :: called URL = ' +
          encodeURI(UrlApis.URL_WPSTATUS_WORKLOAD_GET + '?lang=' + lang + '&bdvnumdoc=' + bdvnumdoc),
      )
      const headerCompleted = await AxiosUtils.getHeader(AxiosUtils.HEADERS.HEADER_READ)
      const response = await axios.get(
        encodeURI(UrlApis.URL_WPSTATUS_WORKLOAD_GET + '?lang=' + lang + '&bdvnumdoc=' + bdvnumdoc),
        {},
        {
          headers: headerCompleted,
          withCredentials: false,
        },
      )

      // Init widget locally
      console.debug('data retrieved <' + t('9500017') + '>')
      setData(response?.data)
    } catch (err) {
      console.debug('ERR <' + t('9500017') + '>: ' + err)
      if (err.response.status === HTTP_CODES.HTTP_CODE_SESSION_EXPIRED) {
        handleDisplayMessage(new Message(MessageSeverity.WARNING, t('9500017'), null, t('sessionExpired')))
        handleRequestLogin()
      } else if (err.response.status === HTTP_CODES.HTTP_CODE_NOT_FOUND) {
        handleDisplayMessage(new Message(MessageSeverity.ERROR, t('9500017'), null, t('9500173')))
      } else {
        handleDisplayMessage(
          new Message(
            MessageSeverity.ERROR,
            t('9500017'),
            err.response.data?.amasisCode,
            err.response.data?.description
              ? err.response.data?.description
              : err.response.data.title
                ? err.response.data.title
                : err.response.data.message
                  ? err.response.data.message
                  : err.response.data.error,
          ),
        )
      }
    } finally {
      handleLoadInProgress(false)
      setIsLoadInProgressActive(false)
    }
  }

  // Sync on receiving data from server (axios)
  useEffect(() => {
    if (bdvnumdoc !== undefined) {
      handleLoadInProgress(true)
      setIsLoadInProgressActive(true)
      loadData()
    }
  }, [bdvnumdoc])

  useEffect(() => {
    if (data && JSON.stringify(data) !== '{}' && JSON.stringify(data) !== '[]') {
      // Sum-up all individual items to determine whether the legend of donuts should be displayed or not
      let sumOfData = 0
      Object.keys(data).forEach((e) => {
        sumOfData += data[e]
      })
      setShowLegend(sumOfData > 0)
    }
  }, [data])

  return (
    <div>
      <Typography variant='large' style={{ marginBottom: '20px' }}>
        {t('9500017')}
      </Typography>
      {data && JSON.stringify(data) !== '{}' && JSON.stringify(data) !== '[]' ? (
        <div className='donutsContainer'>
          <div>
            <div className='workloadDonuts'>
              <div className='workloadOneDonut'>
                <DonutWorkload
                  doughnutKey={'wpstatus_doughnut_A_'}
                  title={t('9500024')}
                  data={[
                    data.opTotalToExecute,
                    data.opToExecute,
                    data.oopToExecute,
                    data.oeTotalToExecute,
                    data.oeTLToExecute,
                    data.oeMCToExecute,
                    data.oeDOCToExecute,
                    data.oeKXToExecute,
                    data.oeMISToExecute,
                    data.tsToExecute,
                  ]}
                  labels={labels}
                  coloursForLabels={coloursForLabels}
                  displayLegend={false}
                />
              </div>
              <div className='workloadOneDonut'>
                <DonutWorkload
                  doughnutKey={'wpstatus_doughnut_B_'}
                  title={t('9500023')}
                  data={[
                    data.hhopTotalToExecute,
                    data.hhopToExecute,
                    data.hhoopToExecute,
                    data.hhoeTotalToExecute,
                    data.hhoeTLToExecute,
                    data.hhoeMCToExecute,
                    data.hhoeDOCToExecute,
                    data.hhoeKXToExecute,
                    data.hhoeMISToExecute,
                    data.hhtsToExecute,
                  ]}
                  labels={labels}
                  coloursForLabels={coloursForLabels}
                  displayLegend={false}
                />
                <div className='tasksWithoutMH'>
                  {t('9500095')}: {data.taskNoHH}
                </div>
              </div>
            </div>
          </div>
          <div className='legendForDonutSections' style={{ visibility: showLegend ? 'visible' : 'collapse' }}>
            {labels &&
              labels.map((label, index) => (
                <div key={'wpstatus_workload_' + index} className='legendOfOneSection'>
                  <div className='colourOfSection' style={{ backgroundColor: coloursForLabels[index] }} />
                  <div className='labelOfSection'>{label}</div>
                </div>
              ))}
          </div>
        </div>
      ) : (
        <div style={{ paddingLeft: '1em' }}>{isLoadInProgressActive ? t('9500166') : t('9500055')}</div>
      )}
    </div>
  )
}

export default WidgetWorkload
